import {
  HelpCircle,
  BookOpen,
  MessageCircle,
  Mail,
  Phone,
  FileText,
  ExternalLink,
  Youtube,
  Search,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

interface HelpSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpSupportModal({ isOpen, onClose }: HelpSupportModalProps) {
  const handleContactSupport = () => {
    toast.success('Solicitud de soporte enviada. Te contactaremos pronto.');
  };

  const supportOptions = [
    {
      icon: BookOpen,
      title: 'Documentación',
      description: 'Guías completas y tutoriales',
      action: 'Ver Docs',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
    },
    {
      icon: MessageCircle,
      title: 'Chat en Vivo',
      description: 'Habla con nuestro equipo de soporte',
      action: 'Iniciar Chat',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      badge: 'Online',
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'soporte@sistema.com',
      action: 'Enviar Email',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
    },
    {
      icon: Phone,
      title: 'Teléfono',
      description: '+52 55 1234 5678',
      action: 'Llamar',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
    },
  ];

  const quickLinks = [
    {
      icon: FileText,
      title: 'Guía de Inicio Rápido',
      description: 'Aprende lo básico en 5 minutos',
    },
    {
      icon: Youtube,
      title: 'Video Tutoriales',
      description: 'Tutoriales paso a paso',
    },
    {
      icon: HelpCircle,
      title: 'Preguntas Frecuentes',
      description: 'Respuestas a dudas comunes',
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span>Ayuda y Soporte</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Search Help */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
            <Input
              placeholder="Buscar en la ayuda..."
              className="border-gray-200 bg-gray-50 pl-9 dark:border-gray-700 dark:bg-gray-800"
            />
          </div>

          {/* Support Options */}
          <div>
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-gray-100">
              ¿Cómo podemos ayudarte?
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {supportOptions.map((option, index) => (
                <Card
                  key={index}
                  className="group cursor-pointer border-gray-200 p-4 transition-all duration-300 hover:border-blue-200 hover:shadow-lg dark:border-gray-800 dark:hover:border-blue-800"
                  onClick={handleContactSupport}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`h-10 w-10 rounded-lg ${option.bgColor} flex flex-shrink-0 items-center justify-center`}
                    >
                      <option.icon className={`h-5 w-5 ${option.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {option.title}
                        </h4>
                        {option.badge && (
                          <Badge className="border-green-200 bg-green-50 text-xs text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            {option.badge}
                          </Badge>
                        )}
                      </div>
                      <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
                        {option.description}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-blue-600 transition-all group-hover:gap-2 dark:text-blue-400">
                        <span>{option.action}</span>
                        <ExternalLink className="h-3 w-3" />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-800" />

          {/* Quick Links */}
          <div>
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-gray-100">Recursos Útiles</h3>
            <div className="space-y-3">
              {quickLinks.map((link, index) => (
                <Card
                  key={index}
                  className="group cursor-pointer border-gray-200 p-4 transition-all duration-300 hover:border-blue-200 hover:shadow-md dark:border-gray-800 dark:hover:border-blue-800"
                  onClick={() => toast.info(`Abriendo: ${link.title}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20">
                        <link.icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {link.title}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {link.description}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-gray-400 transition-colors group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-800" />

          {/* System Information */}
          <div>
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-gray-100">
              Información del Sistema
            </h3>
            <Card className="border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Versión</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">2.5.1</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Última Actualización</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">1 Nov 2025</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Estado del Sistema</p>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Operativo</p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Soporte</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">24/7</p>
                </div>
              </div>
            </Card>
          </div>

          {/* CTA Button */}
          <div className="pt-4">
            <Button
              onClick={handleContactSupport}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-700"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Contactar con Soporte
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
